/** @jsx jsx */
import { jsx } from '@balance-web/core';
import React from 'react';
import Head from 'next/head';
import { Box } from '@balance-web/box';
import { Flex } from '@balance-web/flex';
import { Stack } from '@balance-web/stack';
import { Text } from '@balance-web/text';
import { TextLink } from '@balance-web/text-link';
import { Hidden } from '@balance-web/media-query';

import { Config } from '../../../services/Config';

import type { BaseTheme, ProductTheme } from './types';

export type ThemeDecoratorProps = ProductTheme &
  BaseTheme & {
    title: string;
    publicFolder: string;
    hideFooter?: boolean;
    showPrivacyAndTermsAndCondition?: boolean;
  };

// FIXME: colors should be strongly typed (enums, not strings)
export const ThemeDecorator: React.FC<ThemeDecoratorProps> = ({
  title,
  publicFolder,
  backgroundColor,
  children,
  footerDescription,
  footerLogo,
  leftBackgroundImage,
  rightBackgroundImage,
  textColor,
  headerLogo,
  hideFooter = false,
  showPrivacyAndTermsAndCondition = true,
  textLinkColor,
}) => {
  return (
    <Stack flex={1} minHeight="100svh" width="100%">
      <Head>
        <title>{title}</title>
        <link
          href={Config.APPLICATION_RECKON_LOGO_URL}
          rel="icon"
          sizes="any"
        />
        <link href={`/${publicFolder}/favicon.ico`} rel="apple-touch-icon" />
        <link
          href={`/${publicFolder}/logo-icon.png`}
          rel="apple-touch-startup-image"
        />
        <link href={`/${publicFolder}/logo-icon.png`} rel="apple-touch-icon" />
        <link href={`/${publicFolder}/manifest.webmanifest`} rel="manifest" />
      </Head>
      <Flex flex={1} paddingTop="xxlarge" position="relative">
        <Box
          css={{
            background: backgroundColor,
            zIndex: -1,
          }}
          height="100%"
          left={0}
          position="absolute"
          top={0}
          width="100%"
        >
          <Hidden below="small" css={{ width: '100%' }}>
            <Box
              bottom={0}
              height="max-content"
              left={0}
              position="absolute"
              width={240}
            >
              {leftBackgroundImage}
            </Box>
            <Box
              bottom={0}
              height="max-content"
              position="absolute"
              right={0}
              width={240}
            >
              {rightBackgroundImage}
            </Box>
          </Hidden>
        </Box>
        <Flex
          alignItems="center"
          alignSelf="center"
          direction="column"
          flex={1}
          gap="xlarge"
          height="100%"
          justifyContent="center"
          width="100%"
        >
          {headerLogo}
          <Flex>{children}</Flex>
          {showPrivacyAndTermsAndCondition && (
            <Flex direction="row">
              {Config.APPLICATION_LINK_SERVICETERMS &&
                Config.APPLICATION_LINK_POLICY && (
                  <Text align="center" size="small">
                    By continuing, you agree to our{' '}
                    <TextLink
                      color={textLinkColor}
                      href={Config.APPLICATION_LINK_SERVICETERMS}
                    >
                      Terms of Service{' '}
                    </TextLink>
                    and {<br />}
                    <TextLink
                      color={textLinkColor}
                      href={Config.APPLICATION_LINK_POLICY}
                    >
                      Privacy Policy
                    </TextLink>
                  </Text>
                )}
            </Flex>
          )}

          <Flex
            direction="column"
            flex={1}
            gap="medium"
            justifyContent="flex-end"
            marginBottom="xlarge"
          >
            {!hideFooter && (
              <>
                <Flex justifyContent="center">{footerLogo}</Flex>
                <Text align="center" color="muted" size="xsmall">
                  {footerDescription}
                </Text>
              </>
            )}
          </Flex>
        </Flex>
      </Flex>
    </Stack>
  );
};
